import { AccessLog } from '../MetricasEmpresasPage'

export function extractUniqueRoutes(logs: AccessLog[]): string[] {
    const routesSet = new Set<string>()
    logs.forEach((log) => {
        if (log.rota && log.rota.trim()) {
            routesSet.add(log.rota)
        }
    })
    return Array.from(routesSet).sort()
}

export function extractUniqueClients(logs: AccessLog[]): string[] {
    const clientsSet = new Set<string>()
    logs.forEach((log) => {
        if (log.client && log.client.trim()) {
            clientsSet.add(log.client)
        }
    })
    return Array.from(clientsSet).sort()
}

export interface ILogStats {
    totalRequests: number
    successRate: number
    totalMethods: { [key: string]: number }
    totalStatus: { [key: string]: number }
    totalIPs: number
    averageResponseTime: number
    slowestResponse: number
    fastestResponse: number
    topMethods: { method: string; count: number }[]
    topStatus: { status: string; count: number }[]
    topIPs: { ip: string; count: number }[]
    topRoutes: { rota: string; count: number }[]
    topClients: { client: string; count: number }[]
    requestsByHour: { hour: string; count: number }[]
    methodDistribution: { method: string; percentage: number }[]
    statusDistribution: { status: string; percentage: number }[]
    errorCount: number
    successCount: number
}

export function calculateLogStats(logs: AccessLog[], selectedRoute?: string, selectedClient?: string): ILogStats {
    const stats: ILogStats = {
        totalRequests: logs.length,
        successRate: 0,
        totalMethods: {},
        totalStatus: {},
        totalIPs: 0,
        averageResponseTime: 0,
        slowestResponse: 0,
        fastestResponse: Infinity,
        topMethods: [],
        topStatus: [],
        topIPs: [],
        topRoutes: [],
        topClients: [],
        requestsByHour: [],
        methodDistribution: [],
        statusDistribution: [],
        errorCount: 0,
        successCount: 0
    }

    // Filter logs by route if provided
    let filteredLogs = logs
    if (selectedRoute && selectedRoute !== 'TODOS') {
        filteredLogs = filteredLogs.filter((log) => log.rota === selectedRoute)
    }

    // Filter logs by client if provided
    if (selectedClient && selectedClient !== 'TODOS') {
        filteredLogs = filteredLogs.filter((log) => log.client === selectedClient)
    }

    if (filteredLogs.length === 0) return stats

    // Initialize tracking objects
    const ipSet = new Set<string>()
    const routeCount: { [key: string]: number } = {}
    const clientCount: { [key: string]: number } = {}
    const hourCount: { [key: string]: number } = {}
    let totalResponseTime = 0

    filteredLogs.forEach((log) => {
        // Response Time Analysis
        const responseTime = parseInt(log.responseTime) || 0
        totalResponseTime += responseTime
        if (responseTime > stats.slowestResponse) stats.slowestResponse = responseTime
        if (responseTime < stats.fastestResponse && responseTime > 0) stats.fastestResponse = responseTime

        // IP Analysis
        ipSet.add(log.ip)

        // Method Analysis
        stats.totalMethods[log.method] = (stats.totalMethods[log.method] || 0) + 1

        // Status Analysis
        const statusCode = log.status
        stats.totalStatus[statusCode] = (stats.totalStatus[statusCode] || 0) + 1

        // Success/Error counting
        if (statusCode.startsWith('2')) {
            stats.successCount++
        } else {
            stats.errorCount++
        }

        // Route Analysis
        routeCount[log.rota] = (routeCount[log.rota] || 0) + 1

        // Client Analysis
        if (log.client && log.client.trim()) {
            clientCount[log.client] = (clientCount[log.client] || 0) + 1
        }

        // Hourly Analysis
        try {
            const date = new Date(log.data)
            const hour = `${String(date.getHours()).padStart(2, '0')}:00`
            hourCount[hour] = (hourCount[hour] || 0) + 1
        } catch (e) {
            console.error('Erro ao parsecar data:', e)
        }
    })

    // Calculate metrics
    stats.totalRequests = filteredLogs.length
    stats.totalIPs = ipSet.size
    stats.averageResponseTime = Math.round(totalResponseTime / filteredLogs.length)
    stats.successRate = filteredLogs.length > 0 ? Math.round((stats.successCount / filteredLogs.length) * 100) : 0

    // Top Methods
    stats.topMethods = Object.entries(stats.totalMethods)
        .map(([method, count]) => ({ method, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)

    // Top Status
    stats.topStatus = Object.entries(stats.totalStatus)
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)

    // Top IPs
    const ipCount: { [key: string]: number } = {}
    filteredLogs.forEach((log) => {
        ipCount[log.ip] = (ipCount[log.ip] || 0) + 1
    })
    stats.topIPs = Object.entries(ipCount)
        .map(([ip, count]) => ({ ip, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)

    // Top Routes
    stats.topRoutes = Object.entries(routeCount)
        .map(([rota, count]) => ({ rota, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)

    // Top Clients
    stats.topClients = Object.entries(clientCount)
        .map(([client, count]) => ({ client, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)

    // Requests by Hour
    stats.requestsByHour = Object.entries(hourCount)
        .map(([hour, count]) => ({ hour, count }))
        .sort((a, b) => a.hour.localeCompare(b.hour))

    // Method Distribution
    const totalMethods = stats.topMethods.reduce((sum, m) => sum + m.count, 0)
    stats.methodDistribution = stats.topMethods.map((m) => ({
        method: m.method,
        percentage: Math.round((m.count / totalMethods) * 100)
    }))

    // Status Distribution
    const totalStatus = stats.topStatus.reduce((sum, s) => sum + s.count, 0)
    stats.statusDistribution = stats.topStatus.map((s) => ({
        status: s.status,
        percentage: Math.round((s.count / totalStatus) * 100)
    }))

    // Fix fastest response
    if (stats.fastestResponse === Infinity) {
        stats.fastestResponse = 0
    }

    return stats
}
